import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Icon,
  IconButton,
  Input,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { makeStyles } from '@material-ui/styles'
import { InfoIcon, StarIcon } from 'app/components/icons'
import LanguageRenderItem from 'app/components/selector/LanguageRenderItem'
import SubmitDialog from 'app/componentV4/Dialog/SubmitDialog'
import PrimaryTooltip from 'app/componentV4/Tooltip/PrimaryTooltip'
import Assets from 'app/configs/assetConfigs'
import { CANCEL } from 'app/constants/button'
import clsx from 'clsx'
import { usePlace } from 'commons/usePlace'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  TOUR_CREDIT_USAGE,
  TOUR_CREDIT_WILL_NOT_BE_REFUNDED,
  TOUR_IMPROVE_AND_RINE,
  TOUR_PRE_GENERATED_TOPICS,
  TOUR_WRITE_YOUR_OWN_PROMPT,
} from '../BeginerGuideTour/CMAITour'
import Button from './Button'
import GenerateError from './GenerateError'
import RegenerateContentSection, {
  toneOptions,
} from './RegenerateContentSection'

const maxCustomPromptCharacters = 500

const messageLengthOptions = [
  {
    labelId: 'ai.dialog.chooseMessageLength.short.label',
    descriptionId: 'ai.dialog.chooseMessageLength.short.description',
    id: 'short',
  },
  {
    labelId: 'ai.dialog.chooseMessageLength.medium.label',
    descriptionId: 'ai.dialog.chooseMessageLength.medium.description',
    id: 'medium',
  },
  {
    labelId: 'ai.dialog.chooseMessageLength.long.label',
    descriptionId: 'ai.dialog.chooseMessageLength.long.description',
    id: 'long',
  },
]

const useStyles = makeStyles((theme) => ({
  textarea: {
    '&::placeholder': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
    '&:disabled::placeholder': {
      color: '#d1d5db',
    },
  },
  topics: {
    minHeight: '48px',
  },
}))

export default function GenerateTextWithAIDialog({
  isOpen,
  step,
  isOpenFromTitle,
  prevStep,
  topics,
  selectedTopicIdx,
  error,
  language,
  languages,
  title,
  body,
  onCloseClick,
  onCancelClick,
  onTopicClick,
  onSelectLanguageCode,
  onGenerateContentClick,
  onRegenerateContentClick,
  onUseContentClick,
  tone,
  onToneChange,
  improveOption,
  onImproveOptionChange,
  selectedMessageLengthId,
  onMessageLengthChange,
  selectedCustomPrompt,
  customPrompt,
  onCustomPromptChange,
  onWriteCustomPromptClick,
  onBackClick,
  isGoBackOpen,
  onBackConfirmClick,
  onBackCancelClick,
  credits,
  isTourOpen,
  onExistClick,
}) {
  const classes = useStyles()
  const theme = useTheme()
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'))
  const place = usePlace()
  const isChanging = place.aiBillingCharged
  const { formatMessage } = useIntl()
  if (!isOpen) return null

  const formattedToneOptions = toneOptions(formatMessage)

  const onCloseButton = () => {
    switch (step) {
      case 'prompting':
      case 'error':
        onCloseClick()
        break
      default:
        onExistClick()
        break
    }
  }

  function renderContentByStep() {
    switch (step) {
      case 'prompting': {
        const isGenerateButtonEnabled =
          selectedTopicIdx !== undefined || customPrompt.length > 0
        return (
          <div className="space-y-36">
            <div className="space-y-12">
              {topics && Boolean(topics.length) ? (
                <div className="space-y-8">
                  <div className="flex space-x-4 mb-5 ">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 40 41"
                      className="w-40 h-40"
                    >
                      <path
                        fill="#183560"
                        fillRule="evenodd"
                        d="M26.606 10a.277.277 0 00-.264.194l-.635 2.014a1.5 1.5 0 01-.931.963l-1.83.645a.352.352 0 000 .663l1.83.645a1.5 1.5 0 01.931.964l.635 2.014a.277.277 0 00.528 0l.635-2.014a1.5 1.5 0 01.932-.964l1.828-.645a.352.352 0 000-.663l-1.828-.645a1.5 1.5 0 01-.932-.963l-.635-2.014a.277.277 0 00-.264-.194zm-1.218-.107a1.277 1.277 0 012.436 0l.635 2.014a.5.5 0 00.31.321l1.829.645a1.351 1.351 0 010 2.55l-1.829.644a.5.5 0 00-.31.321l-.635 2.014a1.277 1.277 0 01-2.436 0l-.634-2.014a.5.5 0 00-.311-.321l-1.829-.645a1.352 1.352 0 010-2.549l1.829-.645a.5.5 0 00.31-.321l.635-2.014zm-8.39 5.189a.277.277 0 00-.265.194l-1.535 4.872a1.5 1.5 0 01-.932.964L9.735 22.71a.351.351 0 000 .662l4.53 1.598a1.5 1.5 0 01.933.964l1.535 4.872a.277.277 0 00.528 0l1.536-4.872a1.5 1.5 0 01.932-.964l4.53-1.598a.352.352 0 000-.663l-4.53-1.597a1.5 1.5 0 01-.932-.964l-1.536-4.872a.277.277 0 00-.264-.194zm-1.219-.107a1.277 1.277 0 012.436 0l1.535 4.873a.5.5 0 00.311.32l4.531 1.598a1.351 1.351 0 010 2.55l-4.53 1.597a.5.5 0 00-.312.321l-1.535 4.873a1.277 1.277 0 01-2.436 0l-1.535-4.873a.5.5 0 00-.31-.32l-4.532-1.598a1.352 1.352 0 010-2.55l4.531-1.597a.5.5 0 00.31-.322l1.536-4.872z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <div className="flex flex-col space-y-0">
                      <Typography
                        variant="subtitle2"
                        className="w-full text-primary-400 font-medium"
                      >
                        <FormattedMessage id="ai.dialog.viewSugestedPrompts.title" />
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        className="w-full text-primary-300 font-normal"
                      >
                        <FormattedMessage id="ai.dialog.viewSugestedPrompts.subtitle" />
                      </Typography>
                    </div>
                  </div>
                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 gap-5 auto-rows-fr ${TOUR_PRE_GENERATED_TOPICS}`}
                  >
                    {topics.map((topic, index) => {
                      return (
                        <label
                          className="block cursor-pointer"
                          id={topic}
                          key={topic}
                        >
                          <input
                            type="checkbox"
                            name="topic"
                            className="hidden peer"
                            value={index}
                            checked={index === selectedTopicIdx}
                            onChange={(e) => {
                              onTopicClick(
                                Number(e.target.value) ===
                                  Number(selectedTopicIdx)
                                  ? undefined
                                  : Number(e.target.value),
                              )
                            }}
                          />
                          <div
                            className={clsx(
                              'bg-white text-primary-300 border border-gray-300 flex flex-col space-x-6 items-left justify-center h-m-48 p-8 peer-checked:border-secondary peer-checked:text-secondary rounded-8 peer-checked:shadow text-left transition',
                              classes.topics,
                            )}
                          >
                            <h3 className="text-14 font-medium peer-checked:font-semibold">
                              {topic}
                            </h3>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="py-20 px-16 text-center">
                  <img
                    src={Assets.AILoading}
                    width={120}
                    height={120}
                    alt="Loading"
                  />
                </div>
              )}

              {selectedCustomPrompt ? (
                <div>
                  <div className="rounded-8 border border-secondary disabled:opacity-50 overflow-hidden">
                    <textarea
                      value={customPrompt}
                      onChange={(e) => {
                        onCustomPromptChange(e.target.value)
                      }}
                      rows="4"
                      maxLength={maxCustomPromptCharacters}
                      className={clsx(
                        'block p-12 w-full text-14 text-brue-500 resize-none',
                        classes.textarea,
                      )}
                      placeholder={formatMessage({
                        id: 'ai.dialog.writeYourOwnSubjectOrPrompt.placeholder',
                      })}
                      disabled={!selectedCustomPrompt}
                    ></textarea>
                    <div className="flex justify-end p-12 text-grey-400">
                      <span>
                        {formatMessage({
                          id: 'ai.dialog.writeYourOwnSubjectOrPrompt.characters',
                        })}
                        : {customPrompt ? customPrompt.length : 0}/
                        {maxCustomPromptCharacters}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 flex opacity-50 items-center">
                    <InfoIcon color="#183560" classes="w-20" />
                    <label className="text-12 px-5">
                      <FormattedMessage id="ai.prompt.written.label" />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="flex space-x-8 items-center">
                  <button
                    type="button"
                    className={`text-primary-400 text-left px-2 sm:text-center font-bold text-14 flex items-center space-x-4 rounded hover:bg-gray-200 px-8 py-2 ${TOUR_WRITE_YOUR_OWN_PROMPT}`}
                    onClick={() => {
                      onWriteCustomPromptClick()
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 12 13"
                      className="h-14 w-14 text-primary-400"
                    >
                      <path
                        fill="currentColor"
                        d="M12 6.5a.5.5 0 01-.5.5h-5v5a.5.5 0 01-1 0V7h-5a.5.5 0 010-1h5V1a.5.5 0 011 0v5h5a.5.5 0 01.5.5z"
                      ></path>
                    </svg>
                    <span>
                      <FormattedMessage id="ai.dialog.writeYourOwnSubjectOrPrompt.button" />
                    </span>
                  </button>
                  <PrimaryTooltip
                    title={formatMessage({
                      id: 'ai.dialog.personalize.tooltips',
                    })}
                  >
                    <button
                      type="button"
                      className="h-24 w-24 text-primary-400 rounded-full hover:bg-gray-200 p-4"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 16 17"
                        className="w-full h-full"
                      >
                        <path
                          fill="#183560"
                          d="M7.382 12.375H8.57v-4.75H7.382v4.75zm.534-6.215c.185 0 .34-.06.466-.182a.603.603 0 00.188-.45.662.662 0 00-.188-.48.622.622 0 00-.465-.194.623.623 0 00-.465.194.661.661 0 00-.188.48.6.6 0 00.187.45c.125.122.28.182.465.182zm.006 10.173a7.663 7.663 0 01-3.078-.623 8.044 8.044 0 01-2.518-1.702 8.04 8.04 0 01-1.703-2.52A7.677 7.677 0 010 8.407c0-1.093.208-2.12.623-3.081a7.921 7.921 0 011.703-2.51 8.126 8.126 0 012.52-1.693A7.677 7.677 0 017.926.5c1.093 0 2.12.208 3.081.623.961.416 1.798.98 2.51 1.693a7.994 7.994 0 011.693 2.513c.415.963.623 1.99.623 3.082a7.663 7.663 0 01-.623 3.078 8.086 8.086 0 01-1.692 2.515 7.994 7.994 0 01-2.514 1.702 7.658 7.658 0 01-3.082.627zm.005-1.187c1.867 0 3.453-.657 4.76-1.97 1.306-1.312 1.959-2.902 1.959-4.77 0-1.866-.652-3.453-1.956-4.76-1.304-1.305-2.895-1.958-4.773-1.958-1.86 0-3.447.651-4.76 1.955-1.313 1.304-1.97 2.895-1.97 4.774 0 1.86.657 3.447 1.97 4.76 1.313 1.312 2.903 1.969 4.77 1.969z"
                        ></path>
                      </svg>
                    </button>
                  </PrimaryTooltip>
                </div>
              )}
            </div>

            <div className="space-y-8">
              <div className="flex space-x-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 40 41"
                  className="w-40 h-40"
                >
                  <path
                    fill="#183560"
                    fillRule="evenodd"
                    d="M13.75 24.774a1 1 0 01-1-1v-1.793a1 1 0 01.293-.707l6-6a1 1 0 011.414 0l1.793 1.793a1 1 0 010 1.414l-5.293 5.293h4.293a.5.5 0 010 1h-7.5zm1-1l-1-1v-.793l4.25-4.25 1.793 1.793-4.25 4.25h-.793zm5-7.793l-1.043 1.043 1.793 1.793 1.043-1.043-1.793-1.793z"
                    clipRule="evenodd"
                  ></path>
                  <path
                    fill="#183560"
                    d="M30.21 9.884a1 1 0 01-.622.642l-.574.203a.387.387 0 000 .73l.574.202a1 1 0 01.621.643l.207.655a.353.353 0 00.674 0l.206-.655a1 1 0 01.621-.643l.575-.202a.387.387 0 000-.73l-.575-.203a1 1 0 01-.62-.642l-.207-.656a.353.353 0 00-.674 0l-.207.656zm-4.778 3.609a1 1 0 01-.621.643l-1.803.635a.387.387 0 000 .73l1.803.636a1 1 0 01.621.642l.616 1.955a.353.353 0 00.674 0l.616-1.955a1 1 0 01.621-.642l1.803-.636a.387.387 0 000-.73l-1.803-.635a1 1 0 01-.621-.643l-.616-1.955a.353.353 0 00-.674 0l-.616 1.955z"
                  ></path>
                  <path
                    fill="#183560"
                    fillRule="evenodd"
                    d="M7.25 12.481a1 1 0 011-1h14.792a.5.5 0 010 1H8.75a.5.5 0 00-.5.5v13.744a.5.5 0 00.5.5h6.747a1 1 0 01.922 1.388l-.8 1.902c-.059.14.105.268.227.179l4.477-3.276a1 1 0 01.59-.193h6.837a.5.5 0 00.5-.5v-7.767a.5.5 0 011 0v8.267a1 1 0 01-1 1h-7.337L16 31.82c-.818.599-1.905-.26-1.512-1.195l1.01-2.4H8.25a1 1 0 01-1-1V12.481z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <div className="flex flex-col space-y-0">
                  <Typography
                    variant="subtitle2"
                    className="w-full text-primary-400 font-medium"
                  >
                    <FormattedMessage id="ai.dialog.chooseMessageLength.title" />
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    className="w-full text-primary-300 font-normal"
                  >
                    <FormattedMessage id="ai.dialog.chooseMessageLength.subtitle" />
                  </Typography>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {messageLengthOptions.map((option) => (
                  <label
                    className="block cursor-pointer"
                    id={option.id}
                    key={`messageLengthOptions-${option.id}`}
                  >
                    <input
                      type="checkbox"
                      name="messageLength"
                      className="hidden peer"
                      value={option.id}
                      checked={option.id === selectedMessageLengthId}
                      onChange={(e) => {
                        onMessageLengthChange(
                          e.target.value === selectedMessageLengthId
                            ? undefined
                            : e.target.value,
                        )
                      }}
                    />
                    <div className="bg-white text-primary-300 flex flex-col items-start justify-start border border-gray-300 h-full p-8 peer-checked:border-secondary peer-checked:text-secondary rounded-lg peer-checked:shadow text-center transition">
                      <h3 className="text-14 font-medium peer-checked:font-semibold">
                        <FormattedMessage id={option.labelId} />
                      </h3>
                      <h3 className="text-14 font-normal peer-checked:font-normal">
                        <FormattedMessage id={option.descriptionId} />
                      </h3>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <FormControl
              variant="outlined"
              className="bg-white rounded-8 border-0  w-full"
            >
              <div className="font-medium  text-blue-950 px-1 pb-5">
                <FormattedMessage id="ai.dialog.chooseMessageTone.label" />
              </div>
              <Select
                name="tone-select"
                disableUnderline={true}
                value={tone ?? ''}
                displayEmpty
                renderValue={(selected) =>
                  selected === '' ? (
                    <span className="opacity-50">
                      {formatMessage({ id: 'abby.select.none' })}
                    </span>
                  ) : (
                    formattedToneOptions.find(
                      (option) => option.id === selected,
                    )?.label
                  )
                }
                input={
                  <Input
                    id="tone-select"
                    className="p-14 h-48 rounded-8 border border-grey-input text-blue-950"
                  />
                }
                onChange={(e) => {
                  onToneChange(e.target.value)
                }}
              >
                <MenuItem id="none" value="">
                  <FormattedMessage id="abby.select.none" />
                </MenuItem>
                {formattedToneOptions.map((option) => (
                  <MenuItem
                    id={option.id}
                    value={option.id}
                    key={`toneOptions-${option.id}`}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <div
              className={clsx(
                'flex flex-col-reverse sm:flex-row space-x-0',
                isDownXs ? 'space-x-0' : 'space-x-16',
              )}
            >
              <Button
                onClick={() => {
                  onCloseClick()
                }}
                variant="secondary"
                className="py-8"
              >
                <div className="flex items-center space-x-16">
                  <Typography variant="subtitle2">
                    <FormattedMessage id="ai.dialog.discardAndClose.button" />
                  </Typography>
                </div>
              </Button>
              <Button
                onClick={() => {
                  onGenerateContentClick()
                }}
                disabled={!isGenerateButtonEnabled}
                variant="primary"
                className="py-8 sm:mb-0 mb-5"
              >
                <div className="flex items-center space-x-16 text-white">
                  <StarIcon className="w-24 h-24" />
                  <Typography variant="subtitle2">
                    <FormattedMessage id="abby.button.generate.message" />
                  </Typography>
                </div>
              </Button>
            </div>

            {language && languages && Boolean(languages.length) ? (
              <FormControl variant="outlined">
                <span className="flex items-center space-x-10">
                  <span>
                    <Typography variant="subtitle2">
                      <FormattedMessage id="ai.dialog.generateIn" />
                    </Typography>
                  </span>
                  <Select
                    value={language.code}
                    onChange={(e) => {
                      onSelectLanguageCode(e.target.value)
                    }}
                    className="min-w-192"
                  >
                    {languages.map((language) => (
                      <MenuItem key={language.code} value={language.code}>
                        <LanguageRenderItem language={language} />
                      </MenuItem>
                    ))}
                  </Select>
                </span>
              </FormControl>
            ) : null}
          </div>
        )
      }

      case 'preview': {
        return (
          <div className="space-y-20">
            <Typography variant="subtitle2" className="w-full text-gray-800">
              <FormattedMessage id="abby.preview.dialog.title" />: {title}
            </Typography>
            <div className="space-y-12 bg-primary-200 rounded-8 p-16 min-h-256 h-full max-h-256 md:max-h-360 overflow-y-scroll">
              <div
                className="ai-text-preview"
                dangerouslySetInnerHTML={{ __html: body }}
              ></div>
            </div>
            <RegenerateContentSection
              onRegenerateContentClick={onRegenerateContentClick}
              tone={tone}
              onToneChange={onToneChange}
              improveOption={improveOption}
              onImproveOptionChange={onImproveOptionChange}
              tourClassName={TOUR_IMPROVE_AND_RINE}
            />
            <div className="flex items-center space-x-16">
              {prevStep && !isOpenFromTitle && (
                <>
                  <Button
                    className={clsx(
                      TOUR_CREDIT_WILL_NOT_BE_REFUNDED,
                      'space-x-16 py-8 h-40',
                    )}
                    onClick={() => {
                      onBackClick()
                    }}
                    variant="secondary"
                  >
                    <FormattedMessage id="ai.dialog.back" />
                  </Button>
                </>
              )}

              <Button
                onClick={() => {
                  onUseContentClick()
                }}
                className="space-x-16 py-8"
                variant="primary"
              >
                <StarIcon className="w-24 h-24" />
                <Typography variant="subtitle2">
                  <FormattedMessage id="ai.dialog.useContent" />
                </Typography>
              </Button>
            </div>

            <SubmitDialog
              open={isGoBackOpen}
              type={CANCEL}
              title={formatMessage({
                id: 'ai.dialog.back.dialog.title',
              })}
              description={formatMessage({
                id: 'ai.dialog.back.dialog.description',
              })}
              submitText={formatMessage({
                id: 'ai.dialog.back.dialog.yes',
              })}
              onSubmit={async () => {
                onBackConfirmClick()
              }}
              closeText={formatMessage({
                id: 'text.close',
              })}
              onClose={() => {
                onBackCancelClick()
              }}
            />
          </div>
        )
      }

      case 'generating': {
        return (
          <div className="space-y-20">
            <div className="py-20 px-16 text-center">
              <img
                src={Assets.AILoading}
                width={120}
                height={120}
                alt="Loading"
              />
            </div>

            <Button
              onClick={() => {
                onCancelClick()
              }}
              className="flex justify-center py-8"
              variant="secondary"
            >
              <Typography variant="subtitle2" className="text-gray-700">
                <FormattedMessage id="ai.dialog.generating.cancel" />
              </Typography>
            </Button>
          </div>
        )
      }

      case 'error': {
        return (
          <div className="space-y-8 text-center">
            <GenerateError onRetry={onBackConfirmClick} />
          </div>
        )
      }

      default: {
        return null
      }
    }
  }

  function dialogMaxWidthByStep() {
    switch (step) {
      case 'prompting':
        return 'md'
      case 'topics':
        return 'md'
      case 'preview':
        return 'md'
      case 'generating':
        return 'sm'
      case 'error':
        return 'md'
      default:
        return 'md'
    }
  }

  return (
    <Dialog
      open={isOpen}
      maxWidth={dialogMaxWidthByStep()}
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <div className="w-full sm:hidden block flex justify-end">
          <IconButton
            aria-label="close"
            onClick={onCloseButton}
            className="m-0 p-0"
          >
            <Icon className="text-20">close</Icon>
          </IconButton>
        </div>
        <div className="sm:flex items-center space-x-8 justify-between py-5">
          <div className="flex flex-col">
            <Typography
              variant="subtitle1"
              className="w-full text-primary-400 font-bold"
            >
              <FormattedMessage id="ai.dialog.title" />
            </Typography>
            <div className="flex items-center space-x-8">
              <InfoIcon color="#183560" classes="w-20" />
              <Typography
                variant="subtitle2"
                className="w-full text-primary-300 font-normal"
              >
                <FormattedMessage
                  id={
                    isChanging
                      ? 'ai.dialog.subtitle.prompting.changing'
                      : 'ai.dialog.subtitle.prompting.no.changing'
                  }
                />
              </Typography>
            </div>
          </div>

          <div className="flex items-center">
            <span
              className={`pl-16 pr-20 py-8 rounded-full flex items-center space-x-6 bg-orange-500 text-base text-white ${TOUR_CREDIT_USAGE}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                className="h-24 w-24"
                viewBox="0 0 24 25"
              >
                <path
                  stroke="#fff"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10.773 20.864C6.48 20.864 3 17.2 3 12.682S6.48 4.5 10.773 4.5c4.292 0 7.772 3.663 7.772 8.182s-3.48 8.182-7.772 8.182z"
                ></path>
                <path
                  stroke="#fff"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12.818 10.064a2.932 2.932 0 00-1.84-.655c-1.695 0-3.069 1.465-3.069 3.273s1.374 3.272 3.068 3.272c.69 0 1.328-.243 1.841-.654"
                ></path>
                <path
                  stroke="#fff"
                  strokeWidth="2"
                  d="M10.364 4.5C13.909 4.5 21 5.318 21 12.682c0 7.364-7.091 8.182-10.636 8.182"
                ></path>
              </svg>

              <span>{credits ? credits.toLocaleString() : '-'}</span>
            </span>
            <Divider
              className="min-h-40 ml-16 sm:block hidden"
              variant="inset"
              orientation="vertical"
            />
            <IconButton
              aria-label="close"
              onClick={onCloseButton}
              className="ml-6 sm:block hidden"
            >
              <Icon className="text-20">close</Icon>
            </IconButton>
          </div>
        </div>
      </DialogTitle>
      <DialogContent
        dividers
        className={clsx('pb-20', isTourOpen && 'overflow-hidden')}
      >
        {renderContentByStep()}
      </DialogContent>
    </Dialog>
  )
}
